import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(undefined);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : { items: [], subtotal: 0 };
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (tool) => {
    setCart(prevCart => {
      const existingItem = prevCart.items.find(item => item.tool.id === tool.id);
      
      if (existingItem) {
        return {
          ...prevCart,
          items: prevCart.items.map(item =>
            item.tool.id === tool.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
          subtotal: prevCart.subtotal + tool.price
        };
      }

      return {
        items: [...prevCart.items, { tool, quantity: 1 }],
        subtotal: prevCart.subtotal + tool.price
      };
    });
  };

  const removeFromCart = (toolId) => {
    setCart(prevCart => {
      const itemToRemove = prevCart.items.find(item => item.tool.id === toolId);
      if (!itemToRemove) return prevCart;

      return {
        items: prevCart.items.filter(item => item.tool.id !== toolId),
        subtotal: prevCart.subtotal - (itemToRemove.tool.price * itemToRemove.quantity)
      };
    });
  };

  const updateQuantity = (toolId, quantity) => {
    if (quantity < 1) {
      removeFromCart(toolId);
      return;
    }

    setCart(prevCart => {
      const item = prevCart.items.find(item => item.tool.id === toolId);
      if (!item) return prevCart;

      const quantityDiff = quantity - item.quantity;
      
      return {
        items: prevCart.items.map(item =>
          item.tool.id === toolId
            ? { ...item, quantity }
            : item
        ),
        subtotal: prevCart.subtotal + (item.tool.price * quantityDiff)
      };
    });
  };

  const clearCart = () => {
    setCart({ items: [], subtotal: 0 });
  };

  const itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 